import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { newsApi } from '../utils/api';
import { getImageUrl } from '../config/imageConfig';
import './NewsDetail.css';

function NewsDetail() {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 统一规范新闻数据的字段，兼容后端返回的 camelCase 与 snake_case
  const normalizeNews = (item) => {
    if (!item) return null;
    const n = { ...item };

    // 字段名对齐（优先保留现有使用的 snake_case）
    n.featured_image = n.featured_image || n.featuredImage || n.image || n.imageUrl || null;
    n.published_at = n.published_at || n.publishedAt || n.createdAt || null;
    n.view_count = n.view_count || n.viewCount || n.readCount || 0;

    // 分类兼容：字符串 -> 对象；缺省颜色
    if (n.category && typeof n.category === 'string') {
      n.category = { name: n.category, color: '#2E8B57' };
    }

    // 标签兼容：字符串"a,b" -> ["a","b"]
    if (n.tags && !Array.isArray(n.tags) && typeof n.tags === 'string') {
      n.tags = n.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
    }

    // 相关文章兼容
    const related = n.related_articles || n.relatedArticles || [];
    n.related_articles = Array.isArray(related)
      ? related.map((ra) => {
          const r = { ...ra };
          r.featured_image = r.featured_image || r.featuredImage || r.image || r.imageUrl || null;
          r.published_at = r.published_at || r.publishedAt || r.createdAt || null;
          return r;
        })
      : [];

    // 统一图片URL
    if (n.featured_image) {
      n.featured_image = getImageUrl(n.featured_image);
    }
    n.related_articles = n.related_articles.map((ra) => ({
      ...ra,
      featured_image: ra.featured_image ? getImageUrl(ra.featured_image) : ra.featured_image,
    }));

    return n;
  };

  // API调用辅助函数
  const handleApiCall = async (apiFunction, ...args) => {
    try {
      const response = await apiFunction(...args);
      if (response && response.code === 200) {
        return response.data;
      } else {
        throw new Error(response?.message || '数据获取失败');
      }
    } catch (err) {
      console.error('API调用失败:', err);
      setError(err.message || '网络错误，请稍后重试');
      return null;
    }
  };

  useEffect(() => {
    // 默认占位文章（当接口失败时使用）
    const defaultNews = normalizeNews({
      id: Number(id) || 1,
      title: '示例文章标题（占位）',
      content: '<p>当前后端接口暂不可用，已为您展示示例文章内容以便页面正常浏览。</p><p>待后端服务恢复后，此处将自动显示真实文章内容。</p>',
      summary: '这是文章摘要占位文本。',
      featured_image: '/images/placeholder.jpg',
      author: '庐山植物园',
      publishedAt: new Date().toISOString(),
      viewCount: 0,
      tags: ['示例', '占位'],
      category: { name: '园区新闻', color: '#2E8B57' },
      relatedArticles: []
    });

    const fetchNewsDetail = async () => {
      setLoading(true);
      setError(null);

      const newsData = await handleApiCall(newsApi.getNewsDetail, id);

      if (newsData) {
        setNews(normalizeNews(newsData));
        setError(null); // 有数据则清空错误
      } else {
        // 优雅降级：使用默认占位数据
        setNews(defaultNews);
        setError(null); // 使用占位数据时不显示错误页
      }

      setLoading(false);
    };

    if (id) {
      fetchNewsDetail();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="news-detail-page">
        <div className="container">
          <div className="loading-spinner">加载中...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="news-detail-page">
        <div className="container">
          <div className="error-message">
            <h2>加载失败</h2>
            <p>{error}</p>
            <Link to="/" className="btn btn-primary">返回首页</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="news-detail-page">
        <div className="container">
          <div className="error-message">
            <h2>文章不存在</h2>
            <p>您访问的文章可能已被删除或不存在。</p>
            <Link to="/" className="btn btn-primary">返回首页</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="news-detail-page">
      <div className="container">
        {/* 面包屑导航 */}
        <nav className="breadcrumb">
          <Link to="/">首页</Link>
          <span className="separator">&gt;</span>
          <span className="current">文章详情</span>
        </nav>

        {/* 文章头部 */}
        <header className="news-header">
          <h1 className="news-title">{news.title}</h1>
          
          <div className="news-meta">
            {news.author && (
              <span className="author">
                <i className="icon-user"></i>
                作者：{news.author}
              </span>
            )}
            
            {news.published_at && (
              <span className="publish-date">
                <i className="icon-calendar"></i>
                发布时间：{new Date(news.published_at).toLocaleDateString('zh-CN')}
              </span>
            )}
            
            {Number.isFinite(Number(news.view_count)) && (
              <span className="view-count">
                <i className="icon-eye"></i>
                阅读量：{news.view_count}
              </span>
            )}
          </div>

          {news.category && (
            <div className="news-category">
              <span className="category-tag" style={{backgroundColor: (news.category && news.category.color) || '#2E8B57'}}>
                {news.category && news.category.name ? news.category.name : (typeof news.category === 'string' ? news.category : '资讯')}
              </span>
            </div>
          )}
        </header>

        {/* 特色图片 */}
        {news.featured_image && (
          <div className="news-featured-image">
            <img 
              src={getImageUrl(news.featured_image)} 
              alt={news.title}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}

        {/* 文章摘要 */}
        {news.summary && (
          <div className="news-summary">
            <p>{news.summary}</p>
          </div>
        )}

        {/* 文章内容 */}
        <div className="news-content">
          {news.content ? (
            <div dangerouslySetInnerHTML={{ __html: news.content }} />
          ) : (
            <p>暂无详细内容。</p>
          )}
        </div>

        {/* 标签 */}
        {news.tags && Array.isArray(news.tags) && news.tags.length > 0 && (
          <div className="news-tags">
            <h4>标签：</h4>
            <div className="tags-list">
              {news.tags.map((tag, index) => (
                <span key={index} className="tag">{tag}</span>
              ))}
            </div>
          </div>
        )}

        {/* 相关文章 */}
        {news.related_articles && news.related_articles.length > 0 && (
          <div className="related-articles">
            <h3>相关文章</h3>
            <div className="related-list">
              {news.related_articles.map((article) => (
                <div key={article.id} className="related-item">
                  <Link to={`/news/${article.id}`}>
                    {article.featured_image && (
                      <img 
                        src={getImageUrl(article.featured_image)} 
                        alt={article.title}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="related-content">
                      <h4>{article.title}</h4>
                      <span className="date">
                        {article.published_at ? new Date(article.published_at).toLocaleDateString('zh-CN') : ''}
                      </span>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 返回按钮 */}
        <div className="news-actions">
          <Link to="/" className="btn btn-secondary">返回首页</Link>
        </div>
      </div>
    </div>
  );
}

export default NewsDetail;